import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ViewIds } from '@ardoq/api-types';

import { FlexBox } from '@ardoq/layout';
import { Text } from '@ardoq/typography';
import { t } from '@ardoq/i18n';
import { formatNumber } from '@ardoq/locale';
import {
  resolveCssColor,
  Surface3DChart,
  toCategoryHeightAxis,
  toGridResolution,
  type Surface3DViewSettings,
} from '@ardoq/xyz-charts';
import styled from 'styled-components';
import { VisualizationContainer } from 'tabview/ViewContainer';
import { registerViewPane } from 'tabview/assistantShared/registerViewPane';
import { SURFACE_3D_COLOR_TOKENS } from './surface3dColorTokens';
import { ViewLegend, type ViewLegendRowModel } from '@ardoq/view-legend';
import { ViewLegendContainer } from '@ardoq/graph';
import { noop } from 'lodash';
import type { getViewLegendLabels } from 'viewLegend/getViewLegendLabels';

/**
 * The column the notice and the chart share.
 *
 * `min-height: 0` is the part that matters. A flex item defaults to `min-height: auto`, which
 * refuses to shrink below its content, and the view pane above is a `flex: 1` column with
 * `flex-basis: 0` - so without it the chain has no definite height to resolve against and the
 * canvas collapses to a couple of hundred pixels regardless of the pane's real size.
 */
const Surface3DLayout = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
`;

/**
 * `VisualizationContainer` is the house primitive for this: `position: relative; flex: 1`.
 * Clipped rather than scrolling, because the chart is meant to fit its pane and a scrollbar
 * here would fight the wheel-to-zoom gesture.
 */
const ChartArea = styled(VisualizationContainer)`
  overflow: hidden;
  min-height: 0;
`;

/* `EmptyStatesDoqContainer` centres itself against `height: 100%`, which needs a parent with a
   height to resolve against rather than one sized by its own content. */
const EmptyStateArea = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
`;
import type { Surface3DSourceData } from './sourceData/buildSurfaceSourceData';
import { toAxisScales } from './sourceData/listFields';
import { toListAggregation } from './sourceData/listAggregation';
import type { Surface3DViewProperties } from './types';
import {
  NoAreaToInterpolateDoq,
  Surface3DEmptyState,
  type WidestGap,
} from './Surface3DEmptyState';

export type Surface3DViewProps = Surface3DSourceData & {
  viewSettings: Surface3DViewSettings;
  legendRows: ViewLegendRowModel[];
  viewLegendLabels: ReturnType<typeof getViewLegendLabels>;
};

/**
 * Resolves the theme's chart colours to literals the canvas can use.
 *
 * `colors.surfaceDefault` and friends are CSS custom properties. Assigning one to `fillStyle`
 * is silently ignored - the canvas keeps whatever colour it last had - so they are read off the
 * live element instead, which is what picks up the theme in force when the view mounts.
 *
 * Mount time only. Dark mode is a `body.dark-mode` class toggled from local `useState` in
 * `useDarkMode`, with no action, stream or context behind it, so nothing re-renders this view
 * when it changes - and this memo is keyed on the element, which does not change when a class
 * on `<body>` does. Toggling the theme with the view open leaves the canvas on the old palette
 * until it remounts. No canvas view in the app handles this today; fixing it means making the
 * toggle observable, which is a change to the toggle rather than to this view.
 */
const useChartColors = (element: HTMLElement | null) =>
  useMemo(
    () => ({
      surfaceColor: resolveCssColor(
        element,
        SURFACE_3D_COLOR_TOKENS.surface,
        '#ffffff'
      ),
      inkColor: resolveCssColor(
        element,
        SURFACE_3D_COLOR_TOKENS.ink,
        '#040506'
      ),
      /* The hover tooltip's outline, and nothing else - the grid stopped reading this in
         `gridStroke`. `borderSubtle` is right for a border and has a dark-mode value, which is
         what disqualified it from the grid: a token that does not flip cannot stay a step off a
         plane that does. Kept under the name `gridColor` because that is the argument
         `renderSamples` takes. */
      gridColor: resolveCssColor(
        element,
        SURFACE_3D_COLOR_TOKENS.border,
        '#E6E7E9'
      ),
      /* The axis box's faces. `surfaceDefaultSubtle` rather than a wash of `borderSubtle`, because
         the walls have to stand off whatever is behind them and which direction "one step off"
         goes belongs to the theme: it is `grey50` on a light surface and `grey900` on a dark one. */
      panelColor: resolveCssColor(
        element,
        SURFACE_3D_COLOR_TOKENS.panel,
        '#FAFAFA'
      ),
    }),
    [element]
  );

/**
 * The axis that lost the most components, so the empty state can name a field to change rather
 * than ask for a better spread.
 */
const toWidestGap = (
  excludedByDimension: Record<'x' | 'y' | 'z', number>,
  titles: Record<'x' | 'y' | 'z', string>
): WidestGap | null => {
  const [axis, count] = Object.entries(excludedByDimension).reduce(
    (widest, entry) => (entry[1] > widest[1] ? entry : widest),
    ['x', 0] as [string, number]
  );

  return count === 0
    ? null
    : { axisTitle: titles[axis as 'x' | 'y' | 'z'], count };
};

export const Surface3DView = ({
  points,
  axes,
  binCounts,
  perValueAxes,
  viewSettings,
  xAxisTitle,
  yAxisTitle,
  zAxisTitle,
  isViewEmpty,
  emptyReason,
  excludedByDimension,
  excludedCount,
  axisFields,
  viewInstanceId,
  locale,
  i18nLocale,
  coloring,
  legendRows,
  viewLegendLabels,
  listAxes,
}: Surface3DViewProps) => {
  /* Held in state behind a callback ref rather than in a `useRef`, which is the difference between
     the colours resolving and not. A ref is populated after commit, so reading `.current` during
     render hands `useChartColors` a null on the render that first mounts the layout - and
     `resolveCssColor` then returns its hardcoded light fallbacks. Nothing re-renders this component
     when the ref attaches, so a dark theme kept a white panel with near-black ink on it until some
     unrelated stream emission happened along. `setContainer` is a state update, so the colours are
     resolved against the live element on the very next render. Same pattern `Surface3DChart` uses
     for its own canvas container. A later theme switch is not picked up - see `useChartColors`. */
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  /* The chart publishes its export hook when its controller is built. Held in a ref and read
     through a stable closure so `registerViewPane` does not have to re-run when it arrives -
     the view context is published from an effect keyed on the view's own inputs. */
  const createExportCanvasRef = useRef<(() => OffscreenCanvas | null) | null>(
    null
  );
  const onExportCanvasAvailable = useCallback(
    (createExportCanvas: (() => OffscreenCanvas | null) | null) => {
      createExportCanvasRef.current = createExportCanvas;
    },
    []
  );
  const chartColors = useChartColors(container);
  /* Optimistic, and corrected by the chart's first frame. `setDrawable` fires on every change from
     its initial `null`, so the first decision always arrives; starting at `false` would disable the
     export menu for the frame before it. */
  const [isDrawable, setIsDrawable] = useState(true);

  /* Published for the settings bar, which builds the field pickers from the field list and
     ticks the active options from the settings. Dispatched on change rather than on mount so
     the pickers stay in step as the viewpoint's fields change.

     The view *context* goes with it, and has to. `viewModifiers$` holds one context for the
     whole studio and never clears it on a view change, so a view that publishes properties
     without one inherits the last view's: `viewActionsOptions` has no case for this id, falls to
     its default, and renders Zoom in / Zoom out / Optimize layout built from that stale context.
     On a fresh load it is `DEFAULT_VIEW_CONTEXT`, whose handlers are `noop` rather than null -
     truthy, so the controls render and do nothing. Switch in from the Block Diagram and they are
     wired to its handlers instead. `registerViewPane` leaves all three null, which is what hides
     them, and points `getContainer` at this view's own DOM. */
  useEffect(() => {
    registerViewPane({
      viewId: ViewIds.SURFACE_3D,
      viewProperties: {
        viewSettings,
        axisFields,
        i18nLocale,
        isEmptyView: isViewEmpty,
        isDrawable,
        isListHeight: listAxes.z !== null,
        perValueAxes,
        viewInstanceId,
      } satisfies Surface3DViewProperties,
      isLegendActive: viewSettings.isLegendActive,
      getContainer: () => container,
      createExportCanvas: () => createExportCanvasRef.current?.() ?? null,
      viewInstanceId,
    });
  }, [
    viewSettings,
    axisFields,
    i18nLocale,
    isViewEmpty,
    isDrawable,
    listAxes,
    perValueAxes,
    viewInstanceId,
    container,
  ]);

  const axisScales = useMemo(() => toAxisScales(listAxes), [listAxes]);
  const heightAxis = useMemo(
    () => (listAxes.z ? toCategoryHeightAxis(listAxes.z) : undefined),
    [listAxes]
  );

  const format = useMemo(
    () => (value: number) => formatNumber(value, 'default', locale),
    [locale]
  );

  /* Always shown when anything was left out: a surface built from four fifths of an estate looks
     exactly like one built from all of it.

     The container is mounted whether or not there is anything to say, and only the text inside it
     comes and goes. That is what makes the notice reach a screen reader: most only announce
     changes inside a live region that was already in the DOM, so mounting the region and its
     first message together announces nothing. Padding is still conditional, or an empty region
     would hold an eight-pixel strip above the chart for the whole session.

     Which is why it sits above the empty-state branch rather than inside the chart one. Nothing
     is preselected, so every user starts empty and leaves by picking the third field - the moment
     the count first exists. Returning the doq from a different subtree took the region down with
     it, so that first announcement mounted its own region and said nothing, and the notice
     behaved as though it did not exist until the second time it changed. The text stays gated:
     the empty-state doqs already give their own counts. */
  const exclusionNotice = (
    <FlexBox
      role="status"
      aria-live="polite"
      padding={!isViewEmpty && excludedCount > 0 ? 'small' : 'none'}
      paddingBottom="none"
    >
      {!isViewEmpty && excludedCount > 0 && (
        <Text color="textSubtle" variant="caption">
          {t(
            i18nLocale,
            'views:surface3d.exclusionNotice',
            {
              plotted: points.length,
              total: points.length + excludedCount,
            },
            '{{plotted}} of {{total}} components plotted. The rest have no value for one of the chosen fields.'
          )}
        </Text>
      )}
    </FlexBox>
  );

  if (isViewEmpty) {
    return (
      <Surface3DLayout ref={setContainer}>
        {exclusionNotice}
        <EmptyStateArea>
          <Surface3DEmptyState
            emptyReason={emptyReason}
            excludedCount={excludedCount}
            widestGap={toWidestGap(excludedByDimension, {
              x: xAxisTitle,
              y: yAxisTitle,
              z: zAxisTitle,
            })}
            i18nLocale={i18nLocale}
          />
        </EmptyStateArea>
      </Surface3DLayout>
    );
  }

  return (
    <Surface3DLayout ref={setContainer}>
      {exclusionNotice}
      <ChartArea>
        <ViewLegendContainer visible={viewSettings.isLegendActive}>
          <ViewLegend
            componentTypes={[]}
            referenceTypes={[]}
            additionalRows={legendRows}
            activeDiffMode={null}
            itemClickTrackingFunction={noop}
            labels={viewLegendLabels}
          />
        </ViewLegendContainer>
        <Surface3DChart
          points={points}
          axes={axes}
          onExportCanvasAvailable={onExportCanvasAvailable}
          xTitle={xAxisTitle}
          yTitle={yAxisTitle}
          zTitle={zAxisTitle}
          interpolation={viewSettings.interpolation}
          aggregation={toListAggregation(
            viewSettings.aggregation,
            listAxes.z !== null
          )}
          resolution={toGridResolution(viewSettings, binCounts)}
          ramp={viewSettings.ramp}
          bands={viewSettings.bands}
          coloring={coloring}
          axisScales={axisScales}
          heightAxis={heightAxis}
          contourCount={viewSettings.contourCount}
          style={{
            showWireframe: viewSettings.showWireframe,
            showDrapedGrid: viewSettings.showDrapedGrid,
            showOutlines: viewSettings.showOutlines,
            showDropLines: viewSettings.showDropLines,
            showSamplePositions: viewSettings.showSamplePositions,
          }}
          format={format}
          markListLabel={t(
            i18nLocale,
            'views:surface3d.markList.label',
            'Plotted components'
          )}
          /* The source data catches the two cases it can see - fewer than three positions, and
             all of them on a line - and only for `linear`, which is the only kernel they stop.
             The gridding has one more the app cannot: a triangulation that has area and still
             lays no cell centre inside any of its triangles, which a sliver thinner than
             `1 / resolution` does while sitting well clear of the collinearity tolerance.
             `Surface3DChart` hides both canvases when the grid comes back empty, so without this
             the view is the container's background and nothing else. */
          emptyState={<NoAreaToInterpolateDoq i18nLocale={i18nLocale} />}
          onDrawableChange={setIsDrawable}
          {...chartColors}
        />
      </ChartArea>
    </Surface3DLayout>
  );
};
